import "./B2C.css";
import React, { useMemo, useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";

function downloadHtml(filename, htmlString) {
  const blob = new Blob([htmlString], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function printHtml(title, htmlString) {
  const w = window.open("", "_blank", "noopener,noreferrer,width=980");
  if (!w) return;
  w.document.open();
  w.document.write(`<!doctype html>
<html><head><meta charset="utf-8" />
<title>${title}</title>
<meta name="viewport" content="width=device-width, initial-scale=1" />
<style>
  html,body{margin:0;padding:0}
  body{font-family: Inter,system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;line-height:1.6;color:#111827;padding:28px;max-width:900px}
  h2{margin:0 0 .75rem 0;font-size:1.6rem}
  h4{margin:1.25rem 0 .5rem;font-weight:600}
  p{margin:.5rem 0}
  ul{padding-left:1.25rem;margin:.5rem 0}
  a{color:#2563eb}
  img,iframe{max-width:100%;height:auto}
  *{box-sizing:border-box}
</style></head><body>${htmlString}</body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 150);
}

export default function PrivacyPreview({
  title,
  subtitle = "",
  noticeHtml,
  businessName,
  businessEmail,
  supportEmail
}) {
  const { t } = useTranslation("bookingB2C");
  const [openModal, setOpenModal] = useState(false);
  const [mobileView, setMobileView] = useState(false);

  // lock background scroll when modal open
  useEffect(() => {
    if (openModal) {
      document.documentElement.classList.add("pn-no-scroll");
      document.body.classList.add("pn-no-scroll");
    } else {
      document.documentElement.classList.remove("pn-no-scroll");
      document.body.classList.remove("pn-no-scroll");
    }
    return () => {
      document.documentElement.classList.remove("pn-no-scroll");
      document.body.classList.remove("pn-no-scroll");
    };
  }, [openModal]);

  const resolvedHtml = useMemo(() => {
    let html = noticeHtml || "";
    if (businessName)  html = html.replaceAll("{{BUSINESS_NAME}}", businessName);
    if (businessEmail) html = html.replaceAll("{{BUSINESS_EMAIL}}", businessEmail);
    return html;
  }, [noticeHtml, businessName, businessEmail]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(resolvedHtml);
      alert(t("privacyPreview.alerts.copied"));
    } catch {
      alert(t("privacyPreview.alerts.copyFailed"));
    }
  };

  const effectiveTitle = title || t("onlineBooking.privacy.title"); // reuse existing key

  return (
    <section className="pn-root">
      {!businessEmail ? (
        <div className="sched-alert" style={{ textAlign: "center" }}>
          <span>
            {t("privacyPreview.missingEmail.line1")}{" "}
            {t("privacyPreview.missingEmail.line2")}{" "}
            {t("privacyPreview.missingEmail.line3")}
          </span>
        </div>
      ) : null}

      <header className="pn-head">
        <div>
          <h3 className="pn-title">{effectiveTitle}</h3>
          <p className="pn-sub">{subtitle}</p>
        </div>
        <button className="pn-btn pn-btn-primary pn-btn-sm" onClick={() => setOpenModal(true)}>
          {t("privacyPreview.actions.openAsClient")}
        </button>
      </header>

      <div className="pn-hint">
        {t("privacyPreview.hint.prefix")}{" "}
        <a className="pn-link" href="/support">{t("privacyPreview.hint.linkText")}</a>.
      </div>

      {/* On-page preview */}
      <div className="pn-surface">
        <div className="pn-content" dangerouslySetInnerHTML={{ __html: resolvedHtml }} />
      </div>

      {/* Footer actions */}
      <footer className="pn-footer">
        <div className="pn-footer-spacer" />
        {/* <button className="pn-btn" onClick={() => printHtml(effectiveTitle, resolvedHtml)}>{t("privacyPreview.actions.print")}</button> */}
        <button
          style={{ border: "none" }}
          className="pn-btn"
          onClick={() =>
            downloadHtml(t("privacyPreview.downloadFilename"), resolvedHtml)
          }
        >
          {t("privacyPreview.actions.download")}
        </button>
        {/* <button className="pn-btn pn-btn-outline" onClick={copyToClipboard}>{t("privacyPreview.actions.copyHtml")}</button> */}
      </footer>

      {/* Modal */}
      {openModal && (
        <div
          className="pn-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setOpenModal(false)}
        >
          <div className="pn-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pn-modal-header">
              <h3>{effectiveTitle}</h3>
              <Button
                variant=""
                type="button"
                className="btn-close"
                data-dismiss="modal"
                aria-label={t("privacyPreview.actions.close")}
                onClick={() => setOpenModal(false)}
              />
            </div>

            {/* Centered, no-horizontal-scroll client view */}
            <div className="pn-client-frame">
              <div
                className={`pn-viewport ${mobileView ? "pn-viewport--mobile" : "pn-viewport--desktop"}`}
              >
                <div
                  className="pn-viewport-inner"
                  dangerouslySetInnerHTML={{ __html: resolvedHtml }}
                />
              </div>
            </div>

            <div className="pn-modal-footer">
              <button
                className="pn-btn pn-btn-primary"
                onClick={() => setMobileView((v) => !v)}
                aria-label={
                  mobileView
                    ? t("privacyPreview.aria.switchToDesktop")
                    : t("privacyPreview.aria.switchToMobile")
                }
                title={
                  mobileView
                    ? t("privacyPreview.aria.switchToDesktop")
                    : t("privacyPreview.aria.switchToMobile")
                }
              >
                {mobileView
                  ? t("privacyPreview.view.desktop")
                  : t("privacyPreview.view.mobile")}
              </button>
              <div className="pn-footer-spacer" />
              {/* <button className="pn-btn" onClick={() => printHtml(effectiveTitle, resolvedHtml)}>{t("privacyPreview.actions.print")}</button> */}
              <button
                style={{ border: "none" }}
                className="pn-btn"
                onClick={() =>
                  downloadHtml(t("privacyPreview.downloadFilename"), resolvedHtml)
                }
              >
                {t("privacyPreview.actions.download")}
              </button>
              <button
                className="pn-btn pn-btn-outline"
                onClick={() => setOpenModal(false)}
              >
                {t("privacyPreview.actions.close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
